import { FC } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useTheme
} from "@mui/material";
import { Button } from "@vilocnv/allsetra-core";
import {
  Speed as SpeedIcon,
  CalendarToday as CalendarIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  ConfirmationContent,
  ConfirmationItem,
  ConfirmationLabel,
  ConfirmationValue,
  SystemMileageBadge
} from "./MileageCorrectionConfirmationModal.styled";

interface MileageCorrectionFormData {
  correctionDate: string;
  newMileage: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formData: MileageCorrectionFormData | null;
  systemMileage: number;
}

const MileageCorrectionConfirmationModal: FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  formData,
  systemMileage
}) => {
  const { t, i18n } = useTranslation(["translation", "formFieldsTranslation"]);
  const theme = useTheme();

  if (!formData) return null;

  const formatDate = (dateString: string) => {
    const locale = i18n.language === "en" ? "en-US" : "nl-NL";
    return new Date(dateString).toLocaleDateString(locale, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatMileage = (mileage: number) => {
    return `${mileage.toLocaleString()} km`;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "90vh"
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight={600}>
          {t("typography.confirmMileageCorrection")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("typography.confirmMileageCorrectionDescription")}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <ConfirmationContent>
          <ConfirmationItem>
            <ConfirmationLabel>
              <CalendarIcon sx={{ fontSize: "16px", mr: 1 }} />
              {t("typography.correctionDate")}
            </ConfirmationLabel>
            <ConfirmationValue>
              {formatDate(formData.correctionDate)}
            </ConfirmationValue>
          </ConfirmationItem>

          <ConfirmationItem>
            <ConfirmationLabel>
              <SpeedIcon sx={{ fontSize: "16px", mr: 1 }} />
              {t("typography.newMileage")}
            </ConfirmationLabel>
            <ConfirmationValue>
              {formatMileage(Number(formData.newMileage))}
            </ConfirmationValue>
          </ConfirmationItem>

          <ConfirmationItem>
            <ConfirmationLabel>
              {t("typography.currentMileage")}
            </ConfirmationLabel>
            <SystemMileageBadge>
              <SpeedIcon sx={{ fontSize: "14px", mr: 0.5 }} />
              {formatMileage(systemMileage)}
            </SystemMileageBadge>
          </ConfirmationItem>
        </ConfirmationContent>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button variant="outlined" onClick={onClose} theme={theme}>
          {t("typography.backToEdit")}
        </Button>
        <Button variant="contained" onClick={onConfirm} theme={theme}>
          {t("typography.confirmCorrection")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MileageCorrectionConfirmationModal;
